import * as React from 'react';
import { cloneDeep } from 'lodash';
import { API } from './api';
import { InputNumber } from 'antd';
import { NiCascader } from '@component/index';
import { DefaultOptionType } from 'antd/lib/cascader';
import { isEqual, renderReference } from '@lib/index';
import { FrameType, ParameterType, Reference, ReferenceNode } from '@interface/index';

export interface SelectionOption extends DefaultOptionType {
  valueType?: string;
  payload?: any;
}

export async function getSelectionOptions(key: string, path?: { value: string; value_type: string }[]) {

  const res = await API.common.selection(key, path)
    .catch((err: any) => {
      throw new Error(err.message);
    });

  return parseSelectionOptions(res);
}

const LEAF_SUFFIX = '_leaf';

export const SpecialValueType = {
  Size: 'size',
  Index: 'index'
};

export function parseSelectionOptions(res: any): SelectionOption[] {

  return (res || []).map((item: any) => ({
    value: item.value,
    valueType: item.value_type,
    label: item.label,
    isLeaf: item.is_leaf,
    disabled: item.disabled,
    payload: item.payload
  }));
}

export const SELECTION_KEY = {
  AVAILABLE_ENTITY_TAG: 'AVAILABLE_ENTITY_TAG',
  AVAILABLE_SUB_ENTITY: 'AVAILABLE_SUB_ENTITY',
  AVAILABLE_TYPE_ENTITY: 'AVAILABLE_TYPE_ENTITY',
  AVAILABLE_TYPE_COMPOSITE: 'AVAILABLE_TYPE_COMPOSITE',
  AVAILABLE_TYPE_INTENT: 'AVAILABLE_TYPE_INTENT',
  AVAILABLE_CONDITION: 'AVAILABLE_CONDITION',
  AVAILABLE_SLOT: 'AVAILABLE_SLOT',
  AVAILABLE_LIST_ACTION_VARIABLE: 'AVAILABLE_LIST_ACTION_VARIABLE',
  AVAILABLE_SUGGESTION_SOURCE: 'AVAILABLE_SUGGESTION_SOURCE',
  AVAILABLE_ATTRIBUTE_SUGGESTION_SOURCE: 'AVAILABLE_ATTRIBUTE_SUGGESTION_SOURCE',
  AVAILABLE_ASSOCIATION: 'AVAILABLE_ASSOCIATION',
  AVAILABLE_SUPER_ENTITY: 'AVAILABLE_SUPER_ENTITY',
  AVAILABLE_VALUE_CHECK: 'AVAILABLE_VALUE_CHECK',
  AVAILABLE_ASK_CONDITION: 'AVAILABLE_ASK_CONDITION',
  AVAILABLE_SUB_INSTANCE: 'AVAILABLE_SUB_INSTANCE',
  AVAILABLE_EXTERNAL_ACTION: 'AVAILABLE_EXTERNAL_ACTION',
  AVAILABLE_ENTITY_CONTENT: 'AVAILABLE_ENTITY_CONTENT',
  AVAILABLE_COMPANION: 'AVAILABLE_COMPANION',
  AVAILABLE_ASSOCIATE_TYPE: 'AVAILABLE_ASSOCIATE_TYPE',
  AVAILABLE_USE_EXPRESSION: 'AVAILABLE_USE_EXPRESSION',
  AVAILABLE_INTERFACE_SERVICE: 'AVAILABLE_INTERFACE_SERVICE',
  AVAILABLE_IMPLEMENTATION_SERVICE: 'AVAILABLE_IMPLEMENTATION_SERVICE',
  AVAILABLE_MODULE_SERVICE_IMPLEMENTATION: 'AVAILABLE_MODULE_SERVICE_IMPLEMENTATION',
  AVAILABLE_FUNCTION_PARAMETER: 'AVAILABLE_FUNCTION_PARAMETER',
  AVAILABLE_PSQL_DRIVER: 'AVAILABLE_PSQL_DRIVER',
  LANGUAGE_AGENT_OF_STRUCT: 'LANGUAGE_AGENT_OF_STRUCT',
  CLONE_DESTINATION: 'CLONE_DESTINATION',
  IMPORT_DESTINATION: 'IMPORT_DESTINATION',
  IMPORT_SOURCE: 'IMPORT_SOURCE',
  CLONE_SOURCE: 'CLONE_SOURCE',
  AVAILABLE_DEPARTMENT: 'AVAILABLE_DEPARTMENT',
  AVAILABLE_STATE_UPDATE_STATE: 'AVAILABLE_STATE_UPDATE_STATE',
  AVAILABLE_DIALOG_ACT_INTENT: 'AVAILABLE_DIALOG_ACT_INTENT',
  AVAILABLE_COPILOT_BOT: 'AVAILABLE_COPILOT_BOT'
};

export const SELECTION_VALUE_TYPE = {
  TYPE_ENTITY: 'entity',
  FRAME_INTENT: 'intent',
  FRAME_COMPOSITE: 'composite',
  DATA_QUERY: 'closure',
  ATTRIBUTE: 'attribute'
};

interface NiCommonCascaderProps {
  allowSearch?: boolean;
  className?: string;
  key?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  value?: string[] | Reference;
  selectionkey: string;
  disabled?: boolean;
  rootpath?: { value: string; value_type: string }[];
  optionFilter?: ((option: SelectionOption) => boolean);
  disabledvalues?: string[][];
  onChange?: (value: string[], selectedOptions?: SelectionOption[]) => void;
  displayRender?: (label: string[], selectedOptions?: SelectionOption[]) => React.ReactNode;
  allowClear?: boolean;
}

type NiCommonCascaderState = {
  options: SelectionOption[];
};

export class NiCommonCascader extends React.Component<NiCommonCascaderProps, NiCommonCascaderState> {

  constructor(props: NiCommonCascaderProps) {
    super(props);

    this.state = { options: [] };
  }

  componentDidMount() {
    this.loadData();
  }


  componentDidUpdate(prevProps: any, prevState: any) {

    if (!isEqual(prevProps.selectionkey, this.props.selectionkey)) {
      this.loadData();
      return;
    }

    if (!isEqual(prevProps.disabledvalues, this.props.disabledvalues) || !isEqual(prevState.options, this.state.options)) {
      const nextOptions = this.checkAndFixDisableForOptions(this.state.options);

      this.setState({
        options: nextOptions as SelectionOption[]
      });
    }
  }

  checkAndFixDisableForOptions(options: DefaultOptionType[], preOptions?: DefaultOptionType[]) {
    const props = this.props;
    if (!props.disabledvalues) {
      return options;
    }

    for (const opt of options) {
      if (opt.isLeaf && props.disabledvalues.find((item: string[]) => {
        const tmpValue = cloneDeep(item);

        tmpValue[tmpValue.length - 1] = tmpValue[tmpValue.length - 1] + LEAF_SUFFIX;

        const optionValue = (preOptions || [])
          .map((preOpt: DefaultOptionType) =>
            preOpt.value);
        optionValue.push(opt.value);

        return isEqual(optionValue, tmpValue);
      })) {
        opt.disabled = true;
      } else if (opt.children) {

        const nextPreOptions = preOptions || [];
        nextPreOptions.push(opt);

        opt.children = this.checkAndFixDisableForOptions(opt.children, nextPreOptions);
      } else {
        opt.disabled = false;
      }
    }
    return options;
  }

  onChange = (value: any, selectedOptions: any) => {

    const nextOptions = cloneDeep(selectedOptions);

    if (nextOptions.length > 0) {
      nextOptions[nextOptions.length - 1].value = value[nextOptions.length - 1];
    }

    if (this.props.onChange) {
      this.props.onChange(value, nextOptions);
    }
  };

  supportMultiValueType = () => {

    const key = this.props.selectionkey;
    return key === SELECTION_KEY.AVAILABLE_CONDITION ||
      key === SELECTION_KEY.AVAILABLE_ASK_CONDITION ||
      key === SELECTION_KEY.AVAILABLE_LIST_ACTION_VARIABLE ||
      key === SELECTION_KEY.AVAILABLE_SUGGESTION_SOURCE ||
      key === SELECTION_KEY.AVAILABLE_ATTRIBUTE_SUGGESTION_SOURCE ||
      key === SELECTION_KEY.AVAILABLE_FUNCTION_PARAMETER;
  }

  disableEntityNextType = () => {

    const key = this.props.selectionkey;
    return key === SELECTION_KEY.AVAILABLE_LIST_ACTION_VARIABLE ||
      key === SELECTION_KEY.AVAILABLE_SUGGESTION_SOURCE ||
      key === SELECTION_KEY.AVAILABLE_ATTRIBUTE_SUGGESTION_SOURCE;
  }

  mockConditionLeaf = (selectedOptions?: SelectionOption[]) => {

    const props = this.props;
    const state = this.state;

    const targetOption = selectedOptions && selectedOptions.length && selectedOptions[selectedOptions.length - 1];
    if (targetOption) {
      targetOption.loading = true;
    }

    const payload = targetOption && targetOption.payload;

    const sizeOpt = {
      payload,
      value: 'size',
      label: 'size',
      valueType: SpecialValueType.Size,
      isLeaf: true
    };

    const isComposite = (payload &&
      payload.type &&
      payload.type.type !== FrameType.entity
    );

    const indexOpt: SelectionOption = {
      payload,
      isLeaf: true,
      value: `index${LEAF_SUFFIX}`,
      valueType: SpecialValueType.Index,
      label: 'index'
    };

    indexOpt.label = (
      <span>
        index:
        <span
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        ><InputNumber
            min={0}
            defaultValue={0}
            onChange={(value: any) => {
              this.handleIndexChange(value, indexOpt);
            }}
          />
        </span>
      </span>
    );

    const nextOptions: SelectionOption[] = [];
    if (!this.disableEntityNextType()) {
      nextOptions.push(sizeOpt);
      nextOptions.push(indexOpt);
    }

    if (isComposite) {
      const indexNextOpt = cloneDeep(indexOpt);
      indexNextOpt.value = 'index';
      indexNextOpt.isLeaf = false;
      indexNextOpt.label = (
        <span>
          index:
          <span
            onClick={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          ><InputNumber
              min={0}
              defaultValue={0}
              onChange={(value: any) => {
                this.handleIndexChange(value, indexNextOpt);
              }}
            />
          </span>
        </span>
      );

      nextOptions.push(indexNextOpt);
    }

    let tmpOptions: any = state.options;
    let tmpOption: any;
    for (const opt of selectedOptions || []) {
      tmpOption = tmpOptions.find((item: SelectionOption) => item.value === opt.value);
      tmpOptions = tmpOption.children;
    }
    tmpOption.children = cloneDeep(nextOptions);
    tmpOption.disabled = !nextOptions || !nextOptions.length;
    tmpOption.loading = false;

    this.setState({ options: cloneDeep(state.options) });
    return;
  }

  loadData = (selectedOptions?: SelectionOption[]) => {
    const props = this.props;
    const state = this.state;

    const targetOption = selectedOptions && selectedOptions.length && selectedOptions[selectedOptions.length - 1];
    if (targetOption) {
      targetOption.loading = true;
    }

    const payload = targetOption && targetOption.payload;

    if (this.supportMultiValueType() &&
      targetOption &&
      payload &&
      payload.multi_value &&
      targetOption.valueType !== SpecialValueType.Size &&
      targetOption.valueType !== SpecialValueType.Index
    ) {
      this.mockConditionLeaf(selectedOptions);
      return;
    }

    getSelectionOptions(props.selectionkey,
      [
        ...props.rootpath ? props.rootpath : [],
        ...selectedOptions ? selectedOptions.filter((opt: SelectionOption) =>
          opt.valueType !== SpecialValueType.Size &&
          opt.valueType !== SpecialValueType.Index)
          .map((opt: SelectionOption) =>
            ({ value: opt.value as string, value_type: opt.valueType || '' })) : []
      ]
    ).then((res: SelectionOption[]) => {

      let nextOptions: SelectionOption[] = [];
      for (const resItem of res) {

        if (resItem.isLeaf) {
          const copyItem = cloneDeep(resItem);
          copyItem.value = resItem.value + LEAF_SUFFIX;

          for (const disabledValue of (props.disabledvalues || [])) {
            const tmpValue = cloneDeep(disabledValue);
            tmpValue[tmpValue.length - 1] = tmpValue[tmpValue.length - 1] + LEAF_SUFFIX;
            const selectedValues = (selectedOptions || [])
              .map((selectedOpt: DefaultOptionType) => selectedOpt.value);
            selectedValues.push(resItem.value);
            if (isEqual(selectedValues, tmpValue)) {
              copyItem.disabled = true;
            }
          }
          nextOptions.push(copyItem);
        } else {

          const copyItem = cloneDeep(resItem);

          if (resItem.valueType === ParameterType.INTENT ||
            resItem.valueType === ParameterType.COMPOSITE ||
            resItem.valueType === ParameterType.ENTITY) {
            copyItem.label = `${resItem.label}(Type)`;
          }

          nextOptions.push(copyItem);
        }

        if (this.supportMultiValueType() &&
          !this.disableEntityNextType() &&
          // (resItem.payload &&
          //   resItem.payload.type &&
          //   resItem.payload.type.type === FrameType.entity) &&
          resItem.payload.multi_value &&
          resItem.isLeaf) {
          const copyItem = cloneDeep(resItem);
          copyItem.isLeaf = false;
          nextOptions.push(copyItem);
        }
      }

      if (props.optionFilter) {
        nextOptions = nextOptions.filter((opt: SelectionOption) => {
          return props.optionFilter && props.optionFilter({
            ...opt,
            value: this.removeLeafSuffix(opt.value as string)
          });
        });
      }

      if (selectedOptions) {
        let tmpOptions: any = state.options;
        let tmpOption: any;
        for (const opt of selectedOptions) {
          tmpOption = tmpOptions.find((item: SelectionOption) => item.value === opt.value);

          tmpOptions = tmpOption.children;
        }
        tmpOption.children = nextOptions;
        tmpOption.disabled = !nextOptions || !nextOptions.length;
        tmpOption.loading = false;

        this.setState({ options: cloneDeep(state.options) });

      } else {
        this.setState({ options: nextOptions });
      }
    });
  };

  removeLeafSuffix = (value: string) => {
    if (value.endsWith(LEAF_SUFFIX)) {
      return value.substring(0, value.length - LEAF_SUFFIX.length);
    }
    return value;
  }

  render() {
    const props = this.props;
    const state = this.state;

    const curFrame = this.getCurentFrame();

    let tmpValue: string[] | undefined;


    if (props.value && props.value.length) {
      if (props.value && (typeof props.value[0] !== 'string')) {
        tmpValue = (props.value as Reference).map((item: ReferenceNode) => item.id);
      } else {
        tmpValue = cloneDeep(props.value as string[]);
      }

      if (curFrame
        && curFrame.attributes
        && curFrame.attributes.find((item: string) =>
          tmpValue && tmpValue[0] && item === tmpValue[0])) {
        tmpValue.unshift(curFrame.id);
      }

      tmpValue[tmpValue.length - 1] = tmpValue[tmpValue.length - 1] + LEAF_SUFFIX;
    }

    return <NiCascader
      showSearch
      placement="bottomLeft"
      listHeight={300}
      filterOption={(input, option: any) =>
        (option.label || '').toLowerCase().includes(input.toLowerCase())
      }
      key={props.key}
      disabled={props.disabled}
      className={props.className}
      style={props.style}
      placeholder={props.placeholder}
      value={props.value === null ? null : tmpValue}
      allowClear={props.allowClear}
      options={state.options as DefaultOptionType[]}
      loadData={(selectedOptions?: DefaultOptionType[]) => {
        this.loadData(selectedOptions as (SelectionOption[] | undefined));
      }}
      displayRender={(label: string[], selectedOptions?: DefaultOptionType[]) => {

        if (props.value === null) {
          return null;
        }

        const ret = props.displayRender
          && props.displayRender(
            label,
            selectedOptions as SelectionOption[] | undefined
          )
          || (props.value
            && props.value.length
            && (typeof props.value[0] !== 'string')
            && renderReference(props.value as Reference))
          || label.join('.');

        return ret;
      }}
      onChange={(value: any[], selectedOptions?: DefaultOptionType[]) => {

        const nextVaue = cloneDeep(value);
        const nextOptions = cloneDeep(selectedOptions);

        if (nextVaue && value.length && nextOptions && nextOptions.length) {
          const leafValue = nextVaue[nextVaue.length - 1];
          const leafOpt = nextOptions[nextOptions.length - 1];
          if (leafValue.endsWith(LEAF_SUFFIX)) {
            const realValue = leafValue.substring(0, leafValue.length - LEAF_SUFFIX.length);
            nextVaue[nextVaue.length - 1] = realValue;
            leafOpt.value = realValue;
            nextOptions[nextOptions.length - 1] = leafOpt;
          }
        }

        this.onChange(nextVaue, nextOptions);
      }}
      changeOnSelect={false}
    />;
  }

  getCurentFrame() {
    const cache = JSON.parse(window.sessionStorage.getItem('curFrame') || '{}');

    return cache;
  }

  handleIndexChange = (index: number, option: SelectionOption) => {

    option.index = index;
  }
}

export function renderSelectionVariable(variable: Reference) {
  const variableName = (variable || []).map((node: ReferenceNode) => {
    let name = node.name;
    if (node.showIndex) {
      name = `${name}[${node.index || 0}]`;
    }
    return name;
  }).join('.');

  return variableName;
}

